<template>
    <div v-if="locations.length > 0 && experiences.length > 0" class="pagina-stroom">
        <header class="paginakop">
            <div class="tekst">
                <h1>Bookings</h1>
                <p>{{ statsSubtitle }}</p>
            </div>
        </header>

        <booking-filters :locations="locations" :experiences="experiences" />

        <section class="paneel">
            <div class="paneel-kop" style="flex-wrap: wrap">
                <div class="segment" role="tablist" aria-label="Booking period">
                    <button type="button" role="tab" :aria-selected="!filterStore.past.value" @click="selectTable(false)">
                        Upcoming <span class="n">{{ formatNumber(nextBookings.length) }}</span>
                    </button>
                    <button type="button" role="tab" :aria-selected="filterStore.past.value" @click="selectTable(true)">
                        Past <span class="n">{{ formatNumber(lastBookings.length) }}</span>
                    </button>
                </div>
                <label class="zoek">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
                    <input v-model="query" type="search" placeholder="Search team, email or booking number" autocomplete="off" aria-label="Search bookings" />
                </label>
            </div>

            <template v-if="loading">
                <div class="leeg"><h2>Loading bookings…</h2></div>
            </template>
            <template v-else-if="shownBookings.length">
                <booking-table :bookings="shownBookings" :past="filterStore.past.value" />
                <div class="tabel-voet">
                    <span>Showing {{ formatNumber(shownBookings.length) }} of {{ formatNumber(filteredBookings.length) }}</span>
                    <button v-if="shownBookings.length < filteredBookings.length" class="btn btn-ghost btn-sm" type="button" @click="shown += 50">Show 50 more</button>
                </div>
            </template>
            <div v-else class="leeg">
                <h2>{{ emptyTitle }}</h2>
                <p>{{ emptyText }}</p>
                <router-link class="btn btn-ghost btn-sm" :to="{ name: 'create-booking' }" style="margin-top: 12px">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
                    New booking
                </router-link>
            </div>
        </section>
    </div>
</template>

<script setup>
import dayjs from "dayjs";
import { computed, defineProps, ref, watch } from "vue";
import { Message } from "element-ui";
import { getBookingTable } from "../../api/rest";
import { useUrlFilters } from "../../composables/useUrlFilters";
import BookingFilters from "./Filters.vue";
import BookingTable from "./Table.vue";

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    experiences: {
        type: Array,
        default: () => [],
    },
});

const filterStore = useUrlFilters(
    () => props.locations,
    () => props.experiences,
);

const { filters } = filterStore;

const nextBookings = ref([]);
const lastBookings = ref([]);
const statsData = ref({});
const loading = ref(false);
const query = ref("");
const shown = ref(50);

const formatNumber = (n) => new Intl.NumberFormat("nl-NL").format(n ?? 0);

const selectTable = (past) => {
    filterStore.past.value = past;
    shown.value = 50;
};

const activeList = computed(() => (filterStore.past.value ? lastBookings.value : nextBookings.value));

const filteredBookings = computed(() => {
    const q = query.value.trim().toLowerCase();
    if (!q) {
        return activeList.value;
    }
    return activeList.value.filter((b) =>
        `${b.team_name || ""} ${b.email || ""} ${b.code || ""} ${b.xcube_name || ""}`.toLowerCase().includes(q),
    );
});

const shownBookings = computed(() => filteredBookings.value.slice(0, shown.value));

const statsSubtitle = computed(() => {
    const stats = statsData.value || {};
    const [start, end] = filterStore.range.value;
    const period = `${start.format("ddd D MMM")} – ${end.format("ddd D MMM YYYY")}`;
    const rating = stats.average_rating ? Number(stats.average_rating).toFixed(1) : "–";
    return `${formatNumber(stats.count)} bookings · ${formatNumber(stats.total_number_of_players)} players · average rating ${rating} in ${period}`;
});

const emptyTitle = computed(() => {
    if (query.value.trim()) {
        return `No bookings match "${query.value.trim()}"`;
    }
    return filterStore.past.value ? "No past bookings in this period" : "No upcoming bookings in this period";
});

const emptyText = computed(() => {
    if (query.value.trim()) {
        return "Check the spelling, or search on part of the email address or booking number.";
    }
    return "Pick a longer period or clear a filter. You can also add a booking yourself.";
});

const filterBookings = async () => {
    if (!filters.value) {
        return;
    }
    try {
        loading.value = true;
        const { bookings, stats } = await getBookingTable(filters.value);
        statsData.value = stats;
        const now = new Date();
        lastBookings.value = bookings.filter((b) => new Date(b.date) < now).reverse();
        nextBookings.value = bookings.filter((b) => new Date(b.date) > now);
    } catch (e) {
        Message.error({ message: `Could not load bookings: ${e.message}` });
    } finally {
        loading.value = false;
    }
};

watch(filters, () => {
    shown.value = 50;
    filterBookings();
}, { immediate: true });

watch(query, () => {
    shown.value = 50;
});
</script>
